import React, { useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const PRESETS = [40, 60, 80, 100];

const GiftCards = () => {
  const [amount, setAmount] = useState(60);
  const [custom, setCustom] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const finalAmount = useCustom ? parseFloat(custom) || 0 : amount;

  const buy = async () => {
    if (!email || !name) return toast.error("Please enter your name and email.");
    if (finalAmount < 5 || finalAmount > 500) return toast.error("Amount must be between $5 and $500.");
    setLoading(true);
    try {
      const { data } = await api.post("/giftcards/purchase", {
        amount: finalAmount,
        buyer_email: email,
        buyer_name: name,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-5 py-10" data-testid="giftcards-page">
      <div className="text-xs font-bold uppercase tracking-widest text-[#EC008C]">Gift Cards</div>
      <h1 className="font-display font-black text-4xl md:text-5xl text-black mt-2">Give the gift of Makeba</h1>
      <p className="mt-3 text-[#333] max-w-2xl">Send a MAKEBA Design & Prints gift card. We'll email you a printable card with a unique code you can gift to someone special. No expiration — the balance is theirs to use anytime.</p>

      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <div className="brutal-lg bg-white rounded-3xl p-6 space-y-5">
          <div>
            <div className="text-sm font-bold uppercase tracking-widest mb-2">Choose an amount</div>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((v) => (
                <button
                  key={v}
                  data-testid={`gift-amount-${v}`}
                  onClick={() => { setUseCustom(false); setAmount(v); }}
                  className={`py-3 rounded-xl border-2 border-black font-black ${!useCustom && amount === v ? "bg-[#EC008C] text-white" : "bg-white text-black"}`}
                >
                  ${v}
                </button>
              ))}
            </div>
            <button
              data-testid="gift-amount-custom"
              onClick={() => setUseCustom(true)}
              className={`mt-2 w-full py-3 rounded-xl border-2 border-black font-black ${useCustom ? "bg-[#EC008C] text-white" : "bg-white text-black"}`}
            >
              Custom amount
            </button>
            {useCustom && (
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-widest font-black">Enter amount ($5–$500)</label>
                <input type="number" min="5" max="500" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. 125" className="w-full px-4 py-3 border-2 border-black rounded-xl mt-1" data-testid="gift-custom-input" />
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-black">Your name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border-2 border-black rounded-xl mt-1" data-testid="gift-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-black">Your email (card is sent here)</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border-2 border-black rounded-xl mt-1" data-testid="gift-email-input" />
            </div>
          </div>
          <button
            data-testid="gift-buy-btn"
            onClick={buy}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-black text-white font-black py-4 rounded-full brutal disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Gift size={18} />} Buy ${finalAmount || 0} gift card
          </button>
        </div>

        {/* Preview card */}
        <div className="flex items-center justify-center">
          <div className="w-full rounded-3xl overflow-hidden border-4 border-black brutal-lg bg-[#14141B] text-white">
            <div className="bg-[#EC008C] px-6 py-4 font-black tracking-widest">MAKEBA DESIGN & PRINTS</div>
            <div className="px-6 py-10 text-center">
              <div className="text-xs tracking-[0.3em] uppercase text-[#F4D160]">Gift Card</div>
              <div className="text-6xl font-black my-3">${finalAmount || 0}</div>
              <div className="text-xs text-white/60">Redeemable at makebadesigns.com</div>
              <div className="mt-6 mx-auto max-w-xs bg-[#1E1E29] border border-dashed border-[#EC008C] rounded-xl py-4">
                <div className="text-[10px] tracking-widest uppercase text-white/50">Gift Code</div>
                <div className="text-xl font-black tracking-[0.2em] text-[#F4D160] font-mono">MKB-••••-••••</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default GiftCards;
